/**
 * AffDice SuperAdmin messages — direct edits: the forms a platform user holding the
 * edit capability uses to change an operator's data now, with a reason, the
 * operator told.
 *
 * Every key starts `edit.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const EDITS_MESSAGES = {
  // The dialog every direct edit shares
  'edit.reason': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'edit.reasonHint': {
    en: 'Required. Recorded with the change and sent to the operator.',
    es: 'Obligatorio. Se registra con el cambio y se envía al operador.',
    'pt-BR': 'Obrigatório. Registrado com a alteração e enviado ao operador.',
  },
  'edit.reasonPlaceholder': {
    en: 'e.g. Asked by the operator on ticket 4512',
    es: 'p. ej. Lo pidió el operador en el ticket 4512',
    'pt-BR': 'ex.: Pedido pelo operador no chamado 4512',
  },
  'edit.lands': {
    en: 'This changes the operator\'s data now, under your name, and the operator is told.',
    es: 'Esto cambia los datos del operador ahora, a tu nombre, y se avisa al operador.',
    'pt-BR': 'Isto altera os dados do operador agora, em seu nome, e o operador é avisado.',
  },
  'edit.saveNow': { en: 'Save now', es: 'Guardar ahora', 'pt-BR': 'Salvar agora' },
  'edit.saving': { en: 'Saving…', es: 'Guardando…', 'pt-BR': 'Salvando…' },

  // Affiliate — the actions panel
  'edit.aff.status': { en: 'Change status…', es: 'Cambiar estado…', 'pt-BR': 'Alterar status…' },
  'edit.aff.profile': { en: 'Edit profile…', es: 'Editar perfil…', 'pt-BR': 'Editar perfil…' },
  'edit.aff.tags': { en: 'Tags…', es: 'Etiquetas…', 'pt-BR': 'Etiquetas…' },
  'edit.aff.manager': { en: 'Manager…', es: 'Gestor…', 'pt-BR': 'Gerente…' },
  'edit.aff.note': { en: 'Add note…', es: 'Añadir nota…', 'pt-BR': 'Adicionar nota…' },
  'edit.fact.tags': { en: 'Tags', es: 'Etiquetas', 'pt-BR': 'Etiquetas' },
  'edit.fact.manager': { en: 'Manager', es: 'Gestor', 'pt-BR': 'Gerente' },

  // Affiliate — status
  'edit.status.ACTIVE': { en: 'Active', es: 'Activo', 'pt-BR': 'Ativo' },
  'edit.status.UNDER_REVIEW': { en: 'Under review', es: 'En revisión', 'pt-BR': 'Em análise' },
  'edit.status.SUSPENDED': { en: 'Suspended', es: 'Suspendido', 'pt-BR': 'Suspenso' },
  'edit.status.BLOCKED': { en: 'Blocked', es: 'Bloqueado', 'pt-BR': 'Bloqueado' },
  'edit.status.TERMINATED': { en: 'Terminated', es: 'Terminado', 'pt-BR': 'Encerrado' },
  'edit.status.DEACTIVATED': { en: 'Deactivated', es: 'Desactivado', 'pt-BR': 'Desativado' },
  'edit.status.PENDING': { en: 'Pending', es: 'Pendiente', 'pt-BR': 'Pendente' },
  'edit.status.REJECTED': { en: 'Rejected', es: 'Rechazado', 'pt-BR': 'Rejeitado' },
  'edit.status.platformDeactivation': {
    en: 'A deactivation made here is recorded as the platform\'s: the operator cannot reactivate this affiliate from its own console, only the platform can.',
    es: 'Una desactivación hecha aquí queda registrada como de la plataforma: el operador no puede reactivar a este afiliado desde su propia consola; solo la plataforma puede.',
    'pt-BR': 'Uma desativação feita aqui fica registrada como da plataforma: o operador não pode reativar este afiliado pelo próprio console; só a plataforma pode.',
  },
  'edit.status.title': { en: 'Change status — {name}', es: 'Cambiar estado — {name}', 'pt-BR': 'Alterar status — {name}' },
  'edit.status.now': { en: 'Now: {status}', es: 'Ahora: {status}', 'pt-BR': 'Agora: {status}' },
  'edit.status.to': { en: 'Move to', es: 'Pasar a', 'pt-BR': 'Mudar para' },
  'edit.status.submit': { en: 'Move to {to}', es: 'Pasar a {to}', 'pt-BR': 'Mudar para {to}' },
  'edit.status.final': {
    en: 'This is final: nothing moves an affiliate out of this status again.',
    es: 'Es definitivo: ningún cambio saca a un afiliado de este estado.',
    'pt-BR': 'É definitivo: nada tira um afiliado deste status depois.',
  },
  'edit.status.reinstate': {
    en: 'Reinstating reverses a block or termination. Accrual starts again from now; nothing voided while it was off comes back.',
    es: 'Reincorporar revierte un bloqueo o una terminación. La acumulación vuelve a empezar desde ahora; lo anulado mientras estaba inactivo no vuelve.',
    'pt-BR': 'Reintegrar reverte um bloqueio ou encerramento. O acúmulo recomeça agora; o que foi anulado enquanto estava desligado não volta.',
  },
  'edit.status.force': {
    en: 'Deactivate anyway, over the payable balance',
    es: 'Desactivar de todos modos, pese al saldo pagadero',
    'pt-BR': 'Desativar mesmo assim, apesar do saldo a pagar',
  },
  'edit.status.justification': {
    en: 'Why the balance does not stop it',
    es: 'Por qué el saldo no lo impide',
    'pt-BR': 'Por que o saldo não impede',
  },
  'edit.status.done': {
    en: '{name} is now {to}. The operator has been told.',
    es: '{name} ahora está {to}. Se ha avisado al operador.',
    'pt-BR': '{name} agora está {to}. O operador foi avisado.',
  },

  // Affiliate — profile
  'edit.profile.title': { en: 'Edit profile — {name}', es: 'Editar perfil — {name}', 'pt-BR': 'Editar perfil — {name}' },
  'edit.profile.username': { en: 'Username', es: 'Nombre de usuario', 'pt-BR': 'Nome de usuário' },
  'edit.profile.externalId': { en: 'External ID', es: 'ID externo', 'pt-BR': 'ID externo' },
  'edit.profile.company': { en: 'Company', es: 'Empresa', 'pt-BR': 'Empresa' },
  'edit.profile.contact': { en: 'Contact name', es: 'Nombre de contacto', 'pt-BR': 'Nome do contato' },
  'edit.profile.phone': { en: 'Phone', es: 'Teléfono', 'pt-BR': 'Telefone' },
  'edit.profile.websites': { en: 'Websites', es: 'Sitios web', 'pt-BR': 'Sites' },
  'edit.profile.marketing': { en: 'Marketing note', es: 'Nota de marketing', 'pt-BR': 'Nota de marketing' },
  'edit.profile.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'edit.profile.countryHint': { en: 'Two-letter code, e.g. GB', es: 'Código de dos letras, p. ej. ES', 'pt-BR': 'Código de duas letras, ex.: BR' },
  'edit.profile.geos': { en: 'GEOs worked', es: 'GEOs trabajados', 'pt-BR': 'GEOs trabalhados' },
  'edit.profile.geosHint': { en: 'Country codes, comma-separated', es: 'Códigos de país, separados por comas', 'pt-BR': 'Códigos de país, separados por vírgula' },
  'edit.profile.onlyChanged': {
    en: 'Only the fields you change are sent. Emptying a contact field puts back what the affiliate keeps on their own profile.',
    es: 'Solo se envían los campos que cambias. Vaciar un campo de contacto restaura lo que el afiliado tiene en su propio perfil.',
    'pt-BR': 'Só os campos alterados são enviados. Esvaziar um campo de contato volta ao que o afiliado mantém no próprio perfil.',
  },
  'edit.profile.done': {
    en: '{name}\'s profile is saved. The operator has been told.',
    es: 'Se guardó el perfil de {name}. Se ha avisado al operador.',
    'pt-BR': 'O perfil de {name} foi salvo. O operador foi avisado.',
  },

  // Affiliate — tags
  'edit.tags.title': { en: 'Tags — {name}', es: 'Etiquetas — {name}', 'pt-BR': 'Etiquetas — {name}' },
  'edit.tags.field': { en: 'Tags', es: 'Etiquetas', 'pt-BR': 'Etiquetas' },
  'edit.tags.hint': {
    en: 'Comma-separated. A tag the operator does not have yet is created.',
    es: 'Separadas por comas. Se crea una etiqueta que el operador aún no tiene.',
    'pt-BR': 'Separadas por vírgula. Uma etiqueta que o operador ainda não tem é criada.',
  },
  'edit.tags.placeholder': { en: 'vip, sports', es: 'vip, deportes', 'pt-BR': 'vip, esportes' },
  'edit.tags.clearing': {
    en: 'Every tag will be removed from this affiliate.',
    es: 'Se quitarán todas las etiquetas de este afiliado.',
    'pt-BR': 'Todas as etiquetas serão removidas deste afiliado.',
  },
  'edit.tags.done': {
    en: '{name}\'s tags are saved. The operator has been told.',
    es: 'Se guardaron las etiquetas de {name}. Se ha avisado al operador.',
    'pt-BR': 'As etiquetas de {name} foram salvas. O operador foi avisado.',
  },

  // Affiliate — manager
  'edit.manager.title': { en: 'Manager — {name}', es: 'Gestor — {name}', 'pt-BR': 'Gerente — {name}' },
  'edit.manager.none': { en: 'No manager yet.', es: 'Aún sin gestor.', 'pt-BR': 'Ainda sem gerente.' },
  'edit.manager.current': { en: '{role}: {manager}', es: '{role}: {manager}', 'pt-BR': '{role}: {manager}' },
  'edit.manager.primary': { en: 'Primary', es: 'Principal', 'pt-BR': 'Principal' },
  'edit.manager.secondary': { en: 'Secondary', es: 'Secundario', 'pt-BR': 'Secundário' },
  'edit.manager.person': { en: 'Person', es: 'Persona', 'pt-BR': 'Pessoa' },
  'edit.manager.choose': { en: 'Choose someone on the team', es: 'Elige a alguien del equipo', 'pt-BR': 'Escolha alguém da equipe' },
  'edit.manager.role': { en: 'As', es: 'Como', 'pt-BR': 'Como' },
  'edit.manager.submit': { en: 'Assign', es: 'Asignar', 'pt-BR': 'Atribuir' },
  'edit.manager.noTeam': {
    en: 'This operator has no active team members to assign. Add one under Team first.',
    es: 'Este operador no tiene miembros activos para asignar. Añade uno en Equipo primero.',
    'pt-BR': 'Este operador não tem membros ativos para atribuir. Adicione um em Equipe primeiro.',
  },
  'edit.manager.fromNow': {
    en: 'Takes effect now. Manager commission already earned stays with whoever held the affiliate before.',
    es: 'Se aplica ahora. La comisión de gestor ya ganada queda para quien tenía antes al afiliado.',
    'pt-BR': 'Vale a partir de agora. A comissão de gerente já ganha fica com quem tinha o afiliado antes.',
  },
  'edit.manager.done': {
    en: '{manager} now manages {name}. The operator has been told.',
    es: '{manager} gestiona ahora a {name}. Se ha avisado al operador.',
    'pt-BR': '{manager} agora gerencia {name}. O operador foi avisado.',
  },

  // Affiliate — note
  'edit.note.title': { en: 'Add a note — {name}', es: 'Añadir una nota — {name}', 'pt-BR': 'Adicionar nota — {name}' },
  'edit.note.body': { en: 'Note', es: 'Nota', 'pt-BR': 'Nota' },
  'edit.note.visibility': { en: 'Who sees it', es: 'Quién la ve', 'pt-BR': 'Quem vê' },
  'edit.note.internal': { en: 'The operator\'s staff only', es: 'Solo el personal del operador', 'pt-BR': 'Só a equipe do operador' },
  'edit.note.visible': { en: 'The affiliate too', es: 'También el afiliado', 'pt-BR': 'O afiliado também' },
  'edit.note.submit': { en: 'Add note', es: 'Añadir nota', 'pt-BR': 'Adicionar nota' },
  'edit.note.done': {
    en: 'Note added to {name}. The operator has been told.',
    es: 'Nota añadida a {name}. Se ha avisado al operador.',
    'pt-BR': 'Nota adicionada a {name}. O operador foi avisado.',
  },

  // Operator — company profile
  'edit.company.action': { en: 'Edit company…', es: 'Editar empresa…', 'pt-BR': 'Editar empresa…' },
  'edit.company.title': { en: 'Company profile — {name}', es: 'Perfil de empresa — {name}', 'pt-BR': 'Perfil da empresa — {name}' },
  'edit.company.address': { en: 'Address', es: 'Dirección', 'pt-BR': 'Endereço' },
  'edit.company.addressLine': { en: 'Street address', es: 'Dirección', 'pt-BR': 'Endereço' },
  'edit.company.city': { en: 'City', es: 'Ciudad', 'pt-BR': 'Cidade' },
  'edit.company.region': { en: 'Region', es: 'Región', 'pt-BR': 'Região' },
  'edit.company.postalCode': { en: 'Postal code', es: 'Código postal', 'pt-BR': 'Código postal' },
  'edit.company.countryHint': { en: 'Two-letter code, e.g. MT', es: 'Código de dos letras, p. ej. MT', 'pt-BR': 'Código de duas letras, ex.: MT' },
  'edit.company.countryCode': { en: 'Use a two-letter country code.', es: 'Usa un código de país de dos letras.', 'pt-BR': 'Use um código de país de duas letras.' },
  'edit.company.legalNameRequired': { en: 'An operator needs a legal name.', es: 'Un operador necesita un nombre legal.', 'pt-BR': 'Um operador precisa de razão social.' },
  'edit.company.done': {
    en: 'Company profile saved. The operator has been told.',
    es: 'Perfil de empresa guardado. Se ha avisado al operador.',
    'pt-BR': 'Perfil da empresa salvo. O operador foi avisado.',
  },

  // Operator — settings
  'edit.settings.title': { en: 'Operator settings', es: 'Ajustes del operador', 'pt-BR': 'Configurações do operador' },
  'edit.settings.empty': {
    en: 'No setting can be set at operator level.',
    es: 'Ningún ajuste se puede fijar a nivel de operador.',
    'pt-BR': 'Nenhuma configuração pode ser definida no nível do operador.',
  },
  'edit.settings.setHere': { en: 'Set by this operator', es: 'Fijado por este operador', 'pt-BR': 'Definido por este operador' },
  'edit.settings.changeOne': { en: 'Change {key}', es: 'Cambiar {key}', 'pt-BR': 'Alterar {key}' },
  'edit.settings.clear': {
    en: 'Go back to the platform default ({value}), now',
    es: 'Volver al valor de la plataforma ({value}), ahora',
    'pt-BR': 'Voltar ao padrão da plataforma ({value}), agora',
  },
  'edit.settings.value': { en: 'Value for this operator', es: 'Valor para este operador', 'pt-BR': 'Valor para este operador' },
  'edit.settings.appliesHint': {
    en: 'Left as today, it applies at once. A later day starts at midnight.',
    es: 'Si se deja hoy, se aplica de inmediato. Un día posterior empieza a medianoche.',
    'pt-BR': 'Se ficar hoje, vale na hora. Um dia posterior começa à meia-noite.',
  },
  'edit.settings.clearSubmit': { en: 'Use the platform default', es: 'Usar el valor de la plataforma', 'pt-BR': 'Usar o padrão da plataforma' },

  // Brand
  'edit.brand.profileTitle': { en: 'Brand profile', es: 'Perfil de la marca', 'pt-BR': 'Perfil da marca' },
  'edit.brand.defaultUrlHint': {
    en: 'Where players play. New landing pages, refused clicks and the {brand_url} postback value use it. It can be changed, not removed.',
    es: 'Donde juegan los jugadores. Lo usan las páginas de destino nuevas, los clics rechazados y el valor {brand_url} del postback. Se puede cambiar, no quitar.',
    'pt-BR': 'Onde os jogadores jogam. Novas páginas de destino, cliques recusados e o valor {brand_url} do postback o usam. Pode ser alterado, não removido.',
  },
  'edit.brand.saveNowNote': {
    en: 'Save now changes the brand at once, under your name, and the operator is told. Propose instead sends the name and currency to the operator to approve.',
    es: 'Guardar ahora cambia la marca de inmediato, a tu nombre, y se avisa al operador. Proponer envía el nombre y la moneda al operador para que los apruebe.',
    'pt-BR': 'Salvar agora altera a marca na hora, em seu nome, e o operador é avisado. Propor envia o nome e a moeda para o operador aprovar.',
  },
  'edit.brand.urlNotProposable': {
    en: 'A proposal carries the name and currency only. Save now to change the default page.',
    es: 'Una propuesta lleva solo el nombre y la moneda. Guarda ahora para cambiar la página predeterminada.',
    'pt-BR': 'Uma proposta leva só o nome e a moeda. Salve agora para alterar a página padrão.',
  },
  'edit.brand.saved': {
    en: '{name} is saved. The operator has been told.',
    es: 'Se guardó {name}. Se ha avisado al operador.',
    'pt-BR': '{name} foi salva. O operador foi avisado.',
  },
  'edit.brand.activate': { en: 'Switch on', es: 'Activar', 'pt-BR': 'Ativar' },
  'edit.brand.deactivate': { en: 'Switch off', es: 'Desactivar', 'pt-BR': 'Desativar' },
  'edit.brand.activateTitle': { en: 'Switch on {name}', es: 'Activar {name}', 'pt-BR': 'Ativar {name}' },
  'edit.brand.deactivateTitle': { en: 'Switch off {name}', es: 'Desactivar {name}', 'pt-BR': 'Desativar {name}' },
  'edit.brand.activateNote': {
    en: 'The brand takes traffic again and its campaigns can run.',
    es: 'La marca vuelve a recibir tráfico y sus campañas pueden funcionar.',
    'pt-BR': 'A marca volta a receber tráfego e as campanhas podem rodar.',
  },
  'edit.brand.deactivateNote': {
    en: 'The brand stops taking traffic and its campaigns stop. Switched off from here, only the platform can switch it back on.',
    es: 'La marca deja de recibir tráfico y sus campañas se detienen. Desactivada desde aquí, solo la plataforma puede volver a activarla.',
    'pt-BR': 'A marca para de receber tráfego e as campanhas param. Desativada daqui, só a plataforma pode reativá-la.',
  },

  // Operator — campaigns and deals
  'edit.cd.title': { en: 'Campaigns and deals', es: 'Campañas y acuerdos', 'pt-BR': 'Campanhas e acordos' },
  'edit.cd.note': {
    en: 'Each look is logged against this operator. From here a campaign can be paused, resumed or retired and a deal ended or re-offered.',
    es: 'Cada consulta queda registrada para este operador. Desde aquí se puede pausar, reanudar o retirar una campaña y terminar o volver a ofrecer un acuerdo.',
    'pt-BR': 'Cada consulta é registrada para este operador. Daqui dá para pausar, retomar ou aposentar uma campanha e encerrar ou reofertar um acordo.',
  },
  'edit.cd.view': { en: 'Show campaigns and deals', es: 'Ver campañas y acuerdos', 'pt-BR': 'Ver campanhas e acordos' },
  'edit.cd.campaigns': { en: 'Campaigns', es: 'Campañas', 'pt-BR': 'Campanhas' },
  'edit.cd.deals': { en: 'Deals', es: 'Acuerdos', 'pt-BR': 'Acordos' },
  'edit.cd.noCampaigns': {
    en: 'No campaigns yet. The operator creates them in their own console.',
    es: 'Aún no hay campañas. El operador las crea en su propia consola.',
    'pt-BR': 'Ainda não há campanhas. O operador as cria no próprio console.',
  },
  'edit.cd.noDeals': {
    en: 'No deals yet. Deals are agreed with affiliates in the operator\'s console.',
    es: 'Aún no hay acuerdos. Se pactan con los afiliados en la consola del operador.',
    'pt-BR': 'Ainda não há acordos. Eles são fechados com afiliados no console do operador.',
  },
  'edit.cd.colCampaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'edit.cd.colUpdated': { en: 'Updated', es: 'Actualizada', 'pt-BR': 'Atualizada' },
  'edit.cd.colDeal': { en: 'Deal', es: 'Acuerdo', 'pt-BR': 'Acordo' },
  'edit.cd.colTerms': { en: 'Terms in force', es: 'Condiciones vigentes', 'pt-BR': 'Termos em vigor' },
  'edit.cd.everyAffiliate': { en: 'Brand default', es: 'Predeterminado de la marca', 'pt-BR': 'Padrão da marca' },
  'edit.cd.offerWaiting': { en: 'Offer v{version} awaiting an answer', es: 'Oferta v{version} pendiente de respuesta', 'pt-BR': 'Oferta v{version} aguardando resposta' },
  'edit.cd.stateActive': { en: 'Running', es: 'En marcha', 'pt-BR': 'Rodando' },
  'edit.cd.statePaused': { en: 'Paused', es: 'En pausa', 'pt-BR': 'Pausada' },
  'edit.cd.stateRetired': { en: 'Retired', es: 'Retirada', 'pt-BR': 'Aposentada' },
  'edit.cd.stateDraft': { en: 'Draft', es: 'Borrador', 'pt-BR': 'Rascunho' },
  'edit.cd.running': { en: 'Running', es: 'Vigente', 'pt-BR': 'Vigente' },
  'edit.cd.notAgreed': { en: 'Not agreed yet', es: 'Aún no pactado', 'pt-BR': 'Ainda não fechado' },
  'edit.cd.ends': { en: 'Ends {date}', es: 'Termina el {date}', 'pt-BR': 'Termina em {date}' },
  'edit.cd.ended': { en: 'Ended {date}', es: 'Terminó el {date}', 'pt-BR': 'Encerrado em {date}' },
  'edit.cd.pause': { en: 'Pause', es: 'Pausar', 'pt-BR': 'Pausar' },
  'edit.cd.resume': { en: 'Resume', es: 'Reanudar', 'pt-BR': 'Retomar' },
  'edit.cd.retire': { en: 'Retire', es: 'Retirar', 'pt-BR': 'Aposentar' },
  'edit.cd.end': { en: 'End', es: 'Terminar', 'pt-BR': 'Encerrar' },
  'edit.cd.reactivate': { en: 'Re-offer', es: 'Volver a ofrecer', 'pt-BR': 'Reofertar' },
  'edit.cd.campaignTitle': { en: '{act} {name}', es: '{act} {name}', 'pt-BR': '{act} {name}' },
  'edit.cd.pauseNote': {
    en: 'Its links stop taking new clicks until it is resumed. Nothing already counted moves.',
    es: 'Sus enlaces dejan de aceptar clics nuevos hasta que se reanude. Nada ya contado cambia.',
    'pt-BR': 'Os links param de aceitar cliques novos até ser retomada. Nada já contado muda.',
  },
  'edit.cd.resumeNote': {
    en: 'Its links take new clicks again.',
    es: 'Sus enlaces vuelven a aceptar clics nuevos.',
    'pt-BR': 'Os links voltam a aceitar cliques novos.',
  },
  'edit.cd.retireNote': {
    en: 'Retiring is final: the campaign takes no traffic and no further change. Links already made keep their past clicks.',
    es: 'Retirar es definitivo: la campaña no recibe tráfico ni más cambios. Los enlaces ya creados conservan sus clics.',
    'pt-BR': 'Aposentar é definitivo: a campanha não recebe tráfego nem outras alterações. Links já criados mantêm os cliques.',
  },
  'edit.cd.pausedDone': {
    en: '{name} is paused. The operator has been told.',
    es: '{name} está en pausa. Se ha avisado al operador.',
    'pt-BR': '{name} está pausada. O operador foi avisado.',
  },
  'edit.cd.resumedDone': {
    en: '{name} is running again. The operator has been told.',
    es: '{name} vuelve a estar en marcha. Se ha avisado al operador.',
    'pt-BR': '{name} voltou a rodar. O operador foi avisado.',
  },
  'edit.cd.retiredDone': {
    en: '{name} is retired. The operator has been told.',
    es: '{name} está retirada. Se ha avisado al operador.',
    'pt-BR': '{name} foi aposentada. O operador foi avisado.',
  },
  'edit.cd.endTitle': { en: 'End {name}', es: 'Terminar {name}', 'pt-BR': 'Encerrar {name}' },
  'edit.cd.endNote': {
    en: 'The deal ends now: nothing is earned under it from this moment, nothing earned before moves, and an offer awaiting an answer lapses. Your reason is also the deal\'s own record of why it ended.',
    es: 'El acuerdo termina ahora: no se gana nada con él desde este momento, lo ganado antes no cambia y una oferta pendiente caduca. Tu motivo queda también como el registro del final del acuerdo.',
    'pt-BR': 'O acordo termina agora: nada é ganho com ele a partir deste momento, o que foi ganho antes não muda e uma oferta pendente caduca. Seu motivo também fica como registro do encerramento.',
  },
  'edit.cd.endDone': {
    en: '{name} has ended. The operator has been told.',
    es: '{name} ha terminado. Se ha avisado al operador.',
    'pt-BR': '{name} foi encerrado. O operador foi avisado.',
  },
  'edit.cd.reactivateTitle': { en: 'Re-offer {name}', es: 'Volver a ofrecer {name}', 'pt-BR': 'Reofertar {name}' },
  'edit.cd.reactivateNote': {
    en: 'Offers the affiliate the ended deal\'s last agreed terms again, as a new deal. Nothing runs until the affiliate accepts.',
    es: 'Ofrece al afiliado de nuevo las últimas condiciones pactadas, como un acuerdo nuevo. Nada se aplica hasta que el afiliado acepte.',
    'pt-BR': 'Oferece ao afiliado de novo os últimos termos acordados, como um acordo novo. Nada vale até o afiliado aceitar.',
  },
  'edit.cd.validFrom': { en: 'Terms start', es: 'Inicio de las condiciones', 'pt-BR': 'Início dos termos' },
  'edit.cd.validFromHint': { en: 'Today or later', es: 'Hoy o después', 'pt-BR': 'Hoje ou depois' },
  'edit.cd.reactivateDone': {
    en: '{name} is offered again; it runs once the affiliate accepts. The operator has been told.',
    es: '{name} se ofrece de nuevo; se aplicará cuando el afiliado acepte. Se ha avisado al operador.',
    'pt-BR': '{name} foi reofertado; vale quando o afiliado aceitar. O operador foi avisado.',
  },
  'edit.cd.termsThroughLogin': {
    en: 'Terms and targeting are changed in the operator\'s own forms: log in as one of its users.',
    es: 'Las condiciones y la segmentación se cambian en los formularios del operador: inicia sesión como uno de sus usuarios.',
    'pt-BR': 'Termos e segmentação são alterados nos formulários do operador: entre como um dos usuários dele.',
  },
} as const satisfies Catalogue;
